// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const ATK = {
  lp: { anim: 'jab', speed: 2.5, dur: 16, dmg: 6, range: 1.0, name: 'JAB' },
  mp: { anim: 'punch', speed: 2.0, dur: 26, dmg: 12, range: 1.2, name: 'STRAIGHT' },
  hp: { anim: 'heavypunch', speed: 1.5, dur: 38, dmg: 18, range: 1.4, name: 'HOOK' },
  lk: { anim: 'kick', speed: 2.2, dur: 22, dmg: 7, range: 1.1, name: 'SNAP KICK' },
  mk: { anim: 'kick', speed: 1.8, dur: 28, dmg: 13, range: 1.3, name: 'SIDE KICK' },
  hk: { anim: 'heavykick', speed: 1.3, dur: 40, dmg: 20, range: 1.5, name: 'ROUNDHOUSE' },
};

const ARENA_MIN = -4, ARENA_MAX = 4;
const MIN_DIST = 0.8;
const IDEAL_DIST = 1.5;
const JUMP_VEL = 0.18;
const GRAVITY = 0.008;

const CombatMode = ({
  backgroundImage, characterImage, enemyImage,
  gameTitle, isPlaying, onScoreUpdate, onGameOver, videoUrl,
}) => {
  const outerRef = useRef(null);
  const canvasRef = useRef(null);
  const onScoreRef = useRef(onScoreUpdate);
  const onGameOverRef = useRef(onGameOver);
  const [hud, setHud] = useState({
    playerHp: 100, enemyHp: 100, timer: 99,
    round: 1, playerWins: 0, enemyWins: 0,
    comboCount: 0, comboTimer: 0,
    playerSpecial: 0, enemySpecial: 0,
    phase: 'intro', score: 0, phaseTimer: 0,
  });
  const [hitTexts, setHitTexts] = useState([]);

  useEffect(() => { onScoreRef.current = onScoreUpdate; }, [onScoreUpdate]);
  useEffect(() => { onGameOverRef.current = onGameOver; }, [onGameOver]);

  useEffect(() => {
    if (!isPlaying) return;
    const container = canvasRef.current;
    if (!container) return;
    let running = true;
    const W = container.offsetWidth || 800;
    const H = container.offsetHeight || 600;

    // ═══ THREE.JS SCENE ═══
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a12);

    const camera = new THREE.PerspectiveCamera(50, W / H, 0.1, 100);
    camera.position.set(0, 2.5, 5.0);
    camera.lookAt(0, 1.0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 8, 5);
    scene.add(dirLight);
    const backLight = new THREE.DirectionalLight(0x4466ff, 0.5);
    backLight.position.set(-3, 4, -5);
    scene.add(backLight);

    // Background image — large plane positioned so ground aligns with character feet
    // Extends below y=0 to fill the entire screen with no blank space
    if (backgroundImage) {
      const texLoader = new THREE.TextureLoader();
      texLoader.setCrossOrigin('anonymous');
      texLoader.load(backgroundImage, (tex) => {
        if (!running) return;
        tex.colorSpace = THREE.SRGBColorSpace;
        const bgPlane = new THREE.Mesh(
          new THREE.PlaneGeometry(30, 18),
          new THREE.MeshBasicMaterial({ map: tex })
        );
        // Position so ground (lower third of image) aligns with y=0 (character feet)
        // Plane extends from y=-7 to y=11, covering well below and above
        bgPlane.position.set(0, 2, -7);
        scene.add(bgPlane);
      });
    }

    // Video background
    let videoEl = null;
    if (videoUrl) {
      videoEl = document.createElement('video');
      videoEl.src = videoUrl; videoEl.loop = true; videoEl.muted = true;
      videoEl.playsInline = true; videoEl.play().catch(() => {});
      const vTex = new THREE.VideoTexture(videoEl);
      const vMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(14, 8),
        new THREE.MeshBasicMaterial({ map: vTex, transparent: true, opacity: 0.55 })
      );
      vMesh.position.set(0, 3.5, -6.5);
      scene.add(vMesh);
    }

    // ═══ GAME STATE ═══
    function makeFighter(x, facingRight, hue) {
      return {
        x, hp: 100, maxHp: 100,
        attacking: 0, attackType: '', attackPower: 0, attackHit: false,
        blocking: false, facingRight,
        hitStun: 0, comboCount: 0, comboTimer: 0,
        special: 0, maxSpecial: 100, knockback: 0,
        jumping: false, jumpY: 0, jumpVel: 0,
        model: null, mixer: null, actions: {}, meshes: [],
        currentAnim: 'idle', hue, shield: null, skinSprites: [],
        projectiles: [], aiPatience: 0,
      };
    }

    const gs = {
      frame: 0, score: 0, round: 1, maxRounds: 3,
      playerWins: 0, enemyWins: 0, roundTimer: 99 * 60,
      phase: 'intro', phaseTimer: 0, particles: [], htId: 0,
      player: makeFighter(-0.9, true, 0.58),
      enemy: makeFighter(0.9, false, 0.0),
    };

    const clock = new THREE.Clock();
    const keys = {};
    const gltfLoader = new GLTFLoader();

    // ═══ LOAD FIGHTER ═══
    function loadFighter(fighter, imageUrl) {
      gltfLoader.load('/models/base_fighter.glb', (gltf) => {
        if (!running) return;
        const model = gltf.scene;
        const meshes = [];
        model.traverse(c => {
          if (c.isMesh) {
            c.material = new THREE.MeshStandardMaterial({
              color: new THREE.Color().setHSL(fighter.hue, 0.7, 0.35 + Math.random() * 0.15),
              roughness: 0.55, metalness: 0.05,
              emissive: new THREE.Color(0xff0000), emissiveIntensity: 0,
            });
            meshes.push(c);
          }
        });

        // Apply AI character image as bone-attached sprites (paper-doll skin)
        // Each body part shows its region of the image, attached to its bone
        if (imageUrl) {
          const texLoader = new THREE.TextureLoader();
          texLoader.setCrossOrigin('anonymous');
          texLoader.load(imageUrl, (baseTex) => {
            if (!running) return;
            baseTex.colorSpace = THREE.SRGBColorSpace;
            // Hide 3D mesh — keep skeleton for animation
            meshes.forEach(m => { m.visible = false; });

            // Body part definitions — planes attached to bones for paper-doll effect
            // Includes new bones: Chest, Hands, Feet for better articulation
            const parts = [
              { bone: 'Head',          ox: 0,     oy: 0.30, w: 1.20, h: 1.00, uv: [0.18, 0.68, 0.64, 0.32] },
              { bone: 'Chest',         ox: 0,     oy: 0.02, w: 1.70, h: 0.55, uv: [0.08, 0.48, 0.84, 0.32] },
              { bone: 'Spine',         ox: 0,     oy: 0.00, w: 1.65, h: 0.50, uv: [0.10, 0.34, 0.80, 0.28] },
              { bone: 'Hips',          ox: 0,     oy: 0.10, w: 1.60, h: 0.55, uv: [0.10, 0.24, 0.80, 0.22] },
              { bone: 'LeftUpperArm',  ox:-0.03,  oy: 0.00, w: 0.90, h: 0.42, uv: [0.00, 0.56, 0.28, 0.28] },
              { bone: 'LeftLowerArm',  ox:-0.04,  oy: 0.00, w: 0.85, h: 0.38, uv: [0.00, 0.34, 0.24, 0.26] },
              { bone: 'LeftHand',      ox:-0.04,  oy: 0.00, w: 0.45, h: 0.30, uv: [0.00, 0.22, 0.18, 0.16] },
              { bone: 'RightUpperArm', ox: 0.03,  oy: 0.00, w: 0.90, h: 0.42, uv: [0.72, 0.56, 0.28, 0.28] },
              { bone: 'RightLowerArm', ox: 0.04,  oy: 0.00, w: 0.85, h: 0.38, uv: [0.76, 0.34, 0.24, 0.26] },
              { bone: 'RightHand',     ox: 0.04,  oy: 0.00, w: 0.45, h: 0.30, uv: [0.82, 0.22, 0.18, 0.16] },
              { bone: 'LeftUpperLeg',  ox: 0.00,  oy:-0.04, w: 0.65, h: 0.62, uv: [0.24, 0.06, 0.26, 0.36] },
              { bone: 'LeftLowerLeg',  ox: 0.00,  oy:-0.16, w: 0.62, h: 0.85, uv: [0.22, 0.00, 0.26, 0.22] },
              { bone: 'LeftFoot',      ox: 0.00,  oy:-0.06, w: 0.50, h: 0.45, uv: [0.18, 0.00, 0.26, 0.14] },
              { bone: 'RightUpperLeg', ox: 0.00,  oy:-0.04, w: 0.65, h: 0.62, uv: [0.48, 0.06, 0.26, 0.36] },
              { bone: 'RightLowerLeg', ox: 0.00,  oy:-0.16, w: 0.62, h: 0.85, uv: [0.50, 0.00, 0.26, 0.22] },
              { bone: 'RightFoot',     ox: 0.00,  oy:-0.06, w: 0.50, h: 0.45, uv: [0.54, 0.00, 0.26, 0.14] },
            ];

            const skinSprites = [];
            parts.forEach(p => {
              const boneNode = model.getObjectByName(p.bone);
              if (!boneNode) return;
              const partTex = baseTex.clone();
              partTex.offset.set(p.uv[0], p.uv[1]);
              partTex.repeat.set(p.uv[2], p.uv[3]);
              partTex.wrapS = THREE.ClampToEdgeWrapping;
              partTex.wrapT = THREE.ClampToEdgeWrapping;
              partTex.needsUpdate = true;
              const planeMat = new THREE.MeshBasicMaterial({
                map: partTex, transparent: true, depthWrite: false, side: THREE.DoubleSide,
              });
              const planeGeo = new THREE.PlaneGeometry(p.w * 1.5, p.h);
              const planeMesh = new THREE.Mesh(planeGeo, planeMat);
              planeMesh.position.set(p.ox, p.oy, 0.02);
              planeMesh.frustumCulled = false;
              boneNode.add(planeMesh);
              skinSprites.push(planeMesh);
            });
            fighter.skinSprites = skinSprites;
          }, undefined, () => {
            console.warn('Fighter skin texture failed, keeping 3D model');
          });
        }

        // Scale down model so full body is visible
        // 2D rig: no Y rotation needed. Use scale.x for mirroring.
        const sx = fighter.facingRight ? 0.65 : -0.65;
        model.scale.set(sx, 0.65, 0.65);
        model.position.set(fighter.x, 0, 0);

        const mixer = new THREE.AnimationMixer(model);
        const clips = gltf.animations;
        const actions = {};
        // Load all 8 animation clips
        const animNames = ['Idle', 'Jab', 'Punch', 'HeavyPunch', 'Kick', 'HeavyKick', 'Block', 'Special'];
        animNames.forEach(name => {
          const clip = THREE.AnimationClip.findByName(clips, name);
          if (clip) {
            const key = name.toLowerCase();
            actions[key] = mixer.clipAction(clip);
            if (name === 'Idle') {
              actions[key].setLoop(THREE.LoopRepeat);
              actions[key].play();
            } else {
              actions[key].setLoop(THREE.LoopOnce);
              actions[key].clampWhenFinished = true;
            }
          }
        });
        mixer.addEventListener('finished', () => {
          fighter.currentAnim = 'idle';
          if (actions.idle) actions.idle.reset().play();
        });

        // Blocking shield
        const shield = new THREE.Mesh(
          new THREE.CircleGeometry(0.35, 16),
          new THREE.MeshBasicMaterial({ color: 0x0088ff, transparent: true, opacity: 0, side: THREE.DoubleSide })
        );
        shield.position.set(0, 0.8, 0.4);
        model.add(shield);

        fighter.model = model; fighter.mixer = mixer;
        fighter.actions = actions; fighter.meshes = meshes; fighter.shield = shield;
        scene.add(model);
      }, undefined, (err) => console.error('Fighter load error:', err));
    }

    loadFighter(gs.player, characterImage);
    loadFighter(gs.enemy, enemyImage);

    // ═══ INPUT ═══
    const onKeyDown = e => { keys[e.key] = true; e.preventDefault(); };
    const onKeyUp = e => { keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    function triggerAttack(f, type) {
      if (gs.phase !== 'fight' || f.attacking > 0 || f.hitStun > 0) return;
      const a = ATK[type]; if (!a) return;
      f.attacking = a.dur; f.attackType = type;
      f.attackPower = a.dmg; f.attackHit = false;
      const action = f.actions[a.anim];
      if (action) {
        if (f.actions.idle) f.actions.idle.stop();
        action.timeScale = a.speed;
        action.reset().play();
        f.currentAnim = a.anim;
      }
    }

    function spawnProjectile(f, dir) {
      const color = f === gs.player ? 0x00ddff : 0xff4444;
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.15, 8, 8),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 })
      );
      sphere.position.set(f.x + dir * 0.5, 0.8, 0);
      sphere.add(new THREE.PointLight(color, 2, 3));
      scene.add(sphere);
      f.projectiles.push({ mesh: sphere, vx: dir * 0.12, life: 80 });
    }

    function spawnSparks(x, y, z, count, color) {
      for (let i = 0; i < count; i++) {
        const s = new THREE.Sprite(new THREE.SpriteMaterial({ color, transparent: true }));
        s.position.set(x, y, z);
        s.scale.setScalar(0.06 + Math.random() * 0.04);
        scene.add(s);
        gs.particles.push({
          mesh: s,
          vx: (Math.random() - 0.5) * 0.15,
          vy: Math.random() * 0.08 + 0.02,
          vz: (Math.random() - 0.5) * 0.1,
          life: 20 + Math.random() * 15,
        });
      }
    }

    function addHitText(text, color) {
      const id = ++gs.htId;
      setHitTexts(prev => [...prev.slice(-3), { id, text, color }]);
    }

    // ═══ GAME ACTIONS (mobile buttons from App.js) ═══
    window.gameActions = {
      moveUp: () => { if (gs.phase === 'fight' && !gs.player.jumping) { gs.player.jumping = true; gs.player.jumpVel = JUMP_VEL; } },
      moveDown: () => { if (gs.phase === 'fight') gs.player.blocking = true; },
      moveLeft: () => { if (gs.phase === 'fight' && gs.player.hitStun <= 0) gs.player.x -= 0.06; },
      moveRight: () => { if (gs.phase === 'fight' && gs.player.hitStun <= 0) gs.player.x += 0.06; },
      shoot: () => triggerAttack(gs.player, 'mp'),
      jump: () => window.gameActions.moveUp(),
      lightPunch: () => triggerAttack(gs.player, 'lp'),
      mediumPunch: () => triggerAttack(gs.player, 'mp'),
      hardPunch: () => triggerAttack(gs.player, 'hp'),
      lightKick: () => triggerAttack(gs.player, 'lk'),
      mediumKick: () => triggerAttack(gs.player, 'mk'),
      hardKick: () => triggerAttack(gs.player, 'hk'),
      special: () => {
        if (gs.phase !== 'fight' || gs.player.special < 60 || gs.player.attacking > 0) return;
        gs.player.attacking = 45; gs.player.attackType = 'special';
        gs.player.attackPower = 28; gs.player.attackHit = false; gs.player.special -= 60;
        spawnProjectile(gs.player, gs.player.facingRight ? 1 : -1);
        if (gs.player.actions.special) {
          if (gs.player.actions.idle) gs.player.actions.idle.stop();
          gs.player.actions.special.timeScale = 1.2; gs.player.actions.special.reset().play();
        }
      },
    };

    // ═══ GAME LOGIC ═══
    function checkHit(atk, def, isPlayer) {
      if (atk.attacking <= 0 || atk.attackHit || atk.attackType === 'special') return;
      const a = ATK[atk.attackType]; if (!a) return;
      if (atk.attacking !== Math.floor(a.dur / 2)) return;
      if (Math.abs(atk.x - def.x) >= a.range) return;
      atk.attackHit = true;
      const hitX = (atk.x + def.x) / 2;
      if (!def.blocking) {
        def.hp -= a.dmg; def.hitStun = 15; def.knockback = a.dmg;
        atk.comboCount++; atk.comboTimer = 60;
        atk.special = Math.min(atk.maxSpecial, atk.special + a.dmg * 0.4);
        if (isPlayer) gs.score += a.dmg * 3;
        spawnSparks(hitX, 0.9, 0, 10, isPlayer ? 0x00ddff : 0xff4444);
        const label = atk.comboCount > 1 ? `${atk.comboCount}x ${a.name}!` : `${a.name}!`;
        addHitText(label, isPlayer ? '#FFD700' : '#FF4444');
      } else {
        spawnSparks(hitX, 0.9, 0, 4, 0x4488aa);
        addHitText('BLOCKED', '#888888');
      }
    }

    function updateProjectiles(owner, target, isPlayer) {
      owner.projectiles = owner.projectiles.filter(pr => {
        pr.mesh.position.x += pr.vx; pr.life--;
        if (Math.abs(pr.mesh.position.x - target.x) < 0.5) {
          if (!target.blocking) {
            target.hp -= 28; target.hitStun = 18; target.knockback = 15;
            if (isPlayer) gs.score += 150;
            spawnSparks(target.x, 0.9, 0, 15, isPlayer ? 0x00ddff : 0xff4444);
            addHitText('SPECIAL!', '#00FFFF');
          }
          scene.remove(pr.mesh); return false;
        }
        if (pr.life <= 0 || Math.abs(pr.mesh.position.x) > ARENA_MAX + 2) {
          scene.remove(pr.mesh); return false;
        }
        return true;
      });
    }

    function updateEnemyAI() {
      const e = gs.enemy, p = gs.player;
      if (e.hitStun > 0 || e.attacking > 0) return;
      const d = Math.abs(p.x - e.x);
      // Movement
      if (d > IDEAL_DIST * 1.2) {
        e.x += (p.x > e.x ? 0.028 : -0.028);
      } else if (d < MIN_DIST * 1.3) {
        e.x += (p.x > e.x ? -0.02 : 0.02);
      }
      if (Math.random() < 0.015) e.x += Math.random() > 0.5 ? 0.1 : -0.1;
      if (!e.jumping && Math.random() < 0.006) { e.jumping = true; e.jumpVel = 0.15; }
      // Attack
      e.aiPatience++;
      if (d <= IDEAL_DIST && e.aiPatience > 15 + Math.random() * 25) {
        e.aiPatience = 0;
        const roll = Math.random();
        if (roll < 0.55) {
          triggerAttack(e, ['lp', 'mp', 'hp', 'lk', 'mk', 'hk'][Math.floor(Math.random() * 6)]);
        } else if (roll < 0.7 && e.special >= 60) {
          e.attacking = 45; e.attackType = 'special'; e.attackPower = 28;
          e.attackHit = false; e.special -= 60;
          spawnProjectile(e, e.facingRight ? 1 : -1);
          if (e.actions.punch) { if (e.actions.idle) e.actions.idle.stop(); e.actions.punch.timeScale = 1; e.actions.punch.reset().play(); }
        } else {
          e.blocking = true; setTimeout(() => { if (running) e.blocking = false; }, 200 + Math.random() * 500);
        }
      }
      // Reactive
      if (p.attacking > 5 && d < IDEAL_DIST && Math.random() < 0.25) {
        e.blocking = true; setTimeout(() => { if (running) e.blocking = false; }, 250);
      }
      if (p.attacking > 5 && p.attackType.includes('k') && !e.jumping && Math.random() < 0.12) {
        e.jumping = true; e.jumpVel = 0.16;
      }
    }

    function updateFight() {
      const p = gs.player, e = gs.enemy;
      gs.roundTimer--;
      if (gs.roundTimer <= 0) { gs.phase = 'ko'; gs.phaseTimer = 0; return; }

      // Player input
      if (p.hitStun <= 0) {
        if (keys['ArrowLeft'] || keys['a']) p.x -= 0.04;
        if (keys['ArrowRight'] || keys['d']) p.x += 0.04;
        if ((keys['ArrowUp'] || keys['w']) && !p.jumping) { p.jumping = true; p.jumpVel = JUMP_VEL; }
      }
      p.blocking = !!(keys['ArrowDown'] || keys['s']);
      if (keys['u']) { triggerAttack(p, 'lp'); keys['u'] = false; }
      if (keys['i']) { triggerAttack(p, 'mp'); keys['i'] = false; }
      if (keys['o']) { triggerAttack(p, 'hp'); keys['o'] = false; }
      if (keys['j']) { triggerAttack(p, 'lk'); keys['j'] = false; }
      if (keys['k']) { triggerAttack(p, 'mk'); keys['k'] = false; }
      if (keys['l']) { triggerAttack(p, 'hk'); keys['l'] = false; }
      if (keys['p']) { window.gameActions.special(); keys['p'] = false; }

      // Timers & meters
      [p, e].forEach(f => {
        if (f.attacking > 0) f.attacking--;
        if (f.hitStun > 0) f.hitStun--;
        if (f.comboTimer > 0) f.comboTimer--; else f.comboCount = 0;
        f.special = Math.min(f.maxSpecial, f.special + (f === p ? 0.07 : 0.05));
      });

      // Jumps
      [p, e].forEach(f => {
        if (f.jumping) {
          f.jumpVel -= GRAVITY; f.jumpY += f.jumpVel;
          if (f.jumpY <= 0) { f.jumpY = 0; f.jumping = false; f.jumpVel = 0; }
        }
      });

      // Auto-face each other
      p.facingRight = p.x < e.x;
      e.facingRight = e.x < p.x;

      // Arena bounds
      p.x = Math.max(ARENA_MIN, Math.min(ARENA_MAX, p.x));
      e.x = Math.max(ARENA_MIN, Math.min(ARENA_MAX, e.x));

      // Minimum distance
      const dist = Math.abs(p.x - e.x);
      if (dist < MIN_DIST) {
        const push = (MIN_DIST - dist) / 2;
        if (p.x < e.x) { p.x -= push; e.x += push; } else { p.x += push; e.x -= push; }
        p.x = Math.max(ARENA_MIN, Math.min(ARENA_MAX, p.x));
        e.x = Math.max(ARENA_MIN, Math.min(ARENA_MAX, e.x));
      }

      // Knockback
      [p, e].forEach((f, i) => {
        if (f.knockback > 0) {
          const other = i === 0 ? e : p;
          f.x += (f.x < other.x ? -1 : 1) * f.knockback * 0.02;
          f.knockback *= 0.85;
          if (f.knockback < 0.3) f.knockback = 0;
        }
      });

      // Enemy AI
      updateEnemyAI();
      e.x = Math.max(ARENA_MIN, Math.min(ARENA_MAX, e.x));

      // Hit detection
      checkHit(p, e, true);
      checkHit(e, p, false);

      // Projectiles
      updateProjectiles(p, e, true);
      updateProjectiles(e, p, false);

      if (onScoreRef.current) onScoreRef.current(Math.floor(gs.score));
      if (p.hp <= 0 || e.hp <= 0) { gs.phase = 'ko'; gs.phaseTimer = 0; }
    }

    // ═══ SYNC 3D ═══
    function syncModels() {
      [gs.player, gs.enemy].forEach(f => {
        if (!f.model) return;
        f.model.position.x = f.x;
        f.model.position.y = f.jumpY;
        // 2D mirroring: flip scale.x based on facing direction
        const sx = f.facingRight ? 0.65 : -0.65;
        f.model.scale.x = sx;
        // Hit stun visual
        if (f.skinSprites.length > 0) {
          f.skinSprites.forEach(s => {
            // Tint red during hit stun
            s.material.color.setRGB(f.hitStun > 0 ? 1 : 1, f.hitStun > 0 ? 0.3 : 1, f.hitStun > 0 ? 0.3 : 1);
          });
          f.model.position.z = f.hitStun > 0 ? (Math.random() - 0.5) * 0.04 * f.hitStun : 0;
        } else {
          const ei = f.hitStun > 0 ? f.hitStun * 0.04 : 0;
          f.meshes.forEach(m => { m.material.emissiveIntensity = ei; });
          f.model.position.z = f.hitStun > 0 ? (Math.random() - 0.5) * 0.04 * f.hitStun : 0;
        }
        // Shield + block animation
        if (f.shield) {
          f.shield.material.opacity = f.blocking ? 0.35 : 0;
          f.shield.visible = f.blocking;
          // Trigger block animation when blocking starts
          if (f.blocking && f.currentAnim !== 'block' && f.attacking <= 0) {
            if (f.actions.block) {
              if (f.actions.idle) f.actions.idle.stop();
              f.actions.block.timeScale = 2.0;
              f.actions.block.reset().play();
              f.currentAnim = 'block';
            }
          } else if (!f.blocking && f.currentAnim === 'block') {
            if (f.actions.idle) f.actions.idle.reset().play();
            f.currentAnim = 'idle';
          }
        }
      });
    }

    function syncCamera() {
      const mid = (gs.player.x + gs.enemy.x) / 2;
      const d = Math.abs(gs.player.x - gs.enemy.x);
      const tz = 3.5 + d * 0.35;
      camera.position.x += (mid - camera.position.x) * 0.06;
      camera.position.z += (tz - camera.position.z) * 0.06;
      camera.lookAt(camera.position.x + (mid - camera.position.x) * 0.3, 1.0, 0);
    }

    function tickParticles() {
      gs.particles = gs.particles.filter(p => {
        p.mesh.position.x += p.vx; p.mesh.position.y += p.vy; p.mesh.position.z += p.vz;
        p.vy -= 0.003; p.life--;
        p.mesh.scale.setScalar(Math.max(0.01, (p.life / 35) * 0.06));
        if (p.life <= 0) { scene.remove(p.mesh); p.mesh.material.dispose(); return false; }
        return true;
      });
    }

    function resetRound() {
      gs.round++; gs.roundTimer = 99 * 60;
      [gs.player, gs.enemy].forEach(f => {
        f.hp = f.maxHp; f.attacking = 0; f.hitStun = 0; f.blocking = false;
        f.jumpY = 0; f.jumping = false; f.comboCount = 0; f.knockback = 0;
        f.projectiles.forEach(pr => scene.remove(pr.mesh));
        f.projectiles = [];
      });
      gs.player.x = -0.9; gs.enemy.x = 0.9;
      gs.particles.forEach(p => { scene.remove(p.mesh); p.mesh.material.dispose(); });
      gs.particles = [];
      gs.phase = 'intro';
      gs.phaseTimer = 0;
    }

    // ═══ MAIN LOOP ═══
    let frameId = null;
    function loop() {
      if (!running) return;
      gs.frame++;
      const dt = Math.min(clock.getDelta(), 1 / 30);
      if (gs.player.mixer) gs.player.mixer.update(dt);
      if (gs.enemy.mixer) gs.enemy.mixer.update(dt);

      if (gs.phase === 'intro') {
        gs.phaseTimer++;
        syncModels();
        syncCamera();
        // After 3 seconds, start the fight
        if (gs.phaseTimer > 180) {
          gs.phase = 'fight';
          gs.phaseTimer = 0;
        }
      } else if (gs.phase === 'fight') {
        updateFight();
      } else if (gs.phase === 'ko') {
        gs.phaseTimer++;
        if (gs.phaseTimer > 120) {
          if (gs.enemy.hp <= 0 || gs.player.hp > gs.enemy.hp) { gs.playerWins++; gs.score += 1000; }
          else gs.enemyWins++;
          gs.phase = 'roundEnd'; gs.phaseTimer = 0;
        }
      } else if (gs.phase === 'roundEnd') {
        gs.phaseTimer++;
        if (gs.phaseTimer > 150) {
          if (gs.playerWins >= 2 || gs.enemyWins >= 2 || gs.round >= gs.maxRounds) {
            gs.phase = 'matchEnd'; gs.phaseTimer = 0;
          } else resetRound();
        }
      } else if (gs.phase === 'matchEnd') {
        gs.phaseTimer++;
        if (gs.phaseTimer > 180) {
          if (onGameOverRef.current) onGameOverRef.current(Math.floor(gs.score), gs.playerWins >= 2);
          return;
        }
      }

      syncModels();
      tickParticles();
      syncCamera();
      renderer.render(scene, camera);

      // HUD sync ~10fps
      if (gs.frame % 6 === 0) {
        setHud({
          playerHp: gs.player.hp, enemyHp: gs.enemy.hp,
          timer: Math.max(0, Math.ceil(gs.roundTimer / 60)),
          round: gs.round, playerWins: gs.playerWins, enemyWins: gs.enemyWins,
          comboCount: gs.player.comboCount, comboTimer: gs.player.comboTimer,
          playerSpecial: gs.player.special, enemySpecial: gs.enemy.special,
          phase: gs.phase, score: gs.score, phaseTimer: gs.phaseTimer,
        });
      }

      frameId = requestAnimationFrame(loop);
    }
    frameId = requestAnimationFrame(loop);

    // Resize
    const onResize = () => {
      if (!container) return;
      const w = container.offsetWidth || 800;
      const h = container.offsetHeight || 600;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    // Cleanup
    return () => {
      running = false;
      if (frameId) cancelAnimationFrame(frameId);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('resize', onResize);
      delete window.gameActions;
      if (videoEl) { videoEl.pause(); videoEl.src = ''; }
      // Clean up skin planes
      [gs.player, gs.enemy].forEach(f => {
        f.skinSprites.forEach(s => { s.geometry?.dispose(); s.material.map?.dispose(); s.material.dispose(); });
      });
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      scene.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material.dispose();
        }
      });
    };
  }, [isPlaying, backgroundImage, videoUrl, characterImage, enemyImage]);

  // ═══ HUD RENDER ═══
  const pPct = Math.max(0, (hud.playerHp / 100) * 100);
  const ePct = Math.max(0, (hud.enemyHp / 100) * 100);
  const barColor = (pct) => pct > 50 ? '#00CC44' : pct > 25 ? '#FFD700' : '#FF2222';

  return (
    <div ref={outerRef} className="absolute inset-0 w-full h-full" style={{ touchAction: 'none' }} data-testid="combat-canvas">
      <div ref={canvasRef} className="absolute inset-0" data-testid="combat-3d-canvas" />

      {/* HUD overlay */}
      <div className="absolute inset-0 pointer-events-none z-10" data-testid="combat-hud">
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 py-2 px-3 bg-black/55 flex items-center gap-2">
          {characterImage && (
            <img src={characterImage} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-cyan-400 shrink-0" crossOrigin="anonymous" />
          )}
          <div className="flex-1 min-w-0">
            <div className="h-5 bg-gray-900 rounded overflow-hidden border border-cyan-500/30">
              <div className="h-full rounded transition-all duration-200" style={{ width: `${pPct}%`, background: barColor(pPct) }} data-testid="player-health-bar" />
            </div>
            <div className="text-[11px] text-cyan-400 font-bold mt-0.5 truncate">{(gameTitle || 'PLAYER').toUpperCase()}</div>
          </div>
          <div className="w-14 h-12 bg-black/70 border border-yellow-500/50 rounded flex items-center justify-center shrink-0" data-testid="round-timer">
            <span className={`text-2xl font-bold font-mono ${hud.timer <= 10 ? 'text-red-500 animate-pulse' : 'text-yellow-400'}`}>{hud.timer}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="h-5 bg-gray-900 rounded overflow-hidden border border-red-500/30">
              <div className="h-full rounded transition-all duration-200 ml-auto" style={{ width: `${ePct}%`, background: barColor(ePct) }} data-testid="enemy-health-bar" />
            </div>
            <div className="text-[11px] text-red-400 font-bold mt-0.5 text-right">ENEMY</div>
          </div>
          {enemyImage && (
            <img src={enemyImage} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-red-500 shrink-0" crossOrigin="anonymous" />
          )}
        </div>

        {/* Special meters + round dots */}
        <div className="absolute top-[60px] left-3 right-3 flex justify-between items-center">
          <div className="w-28">
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all" style={{ width: `${hud.playerSpecial}%` }} data-testid="player-special" />
            </div>
            {hud.playerSpecial >= 60 && <span className="text-[8px] text-cyan-400 font-bold">SPECIAL READY</span>}
          </div>
          <div className="flex gap-4">
            <div className="flex gap-1">{[0,1,2].map(i => <div key={i} className={`w-2.5 h-2.5 rounded-full ${i < hud.playerWins ? 'bg-cyan-400' : 'bg-white/10'}`} />)}</div>
            <div className="flex gap-1">{[0,1,2].map(i => <div key={i} className={`w-2.5 h-2.5 rounded-full ${i < hud.enemyWins ? 'bg-red-500' : 'bg-white/10'}`} />)}</div>
          </div>
          <div className="w-28">
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 transition-all ml-auto" style={{ width: `${hud.enemySpecial}%` }} data-testid="enemy-special" />
            </div>
          </div>
        </div>

        {/* Hit texts */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
          {hitTexts.map(t => (
            <div
              key={t.id}
              className="text-2xl font-black whitespace-nowrap"
              style={{ color: t.color, textShadow: '0 2px 8px rgba(0,0,0,0.9)', animation: 'combatHitFloat 0.9s ease-out forwards' }}
              onAnimationEnd={() => setHitTexts(prev => prev.filter(x => x.id !== t.id))}
            >
              {t.text}
            </div>
          ))}
        </div>

        {/* Combo counter */}
        {hud.comboCount > 1 && hud.comboTimer > 0 && (
          <div className="absolute left-4 bottom-6" data-testid="combo-counter">
            <div className="text-yellow-400 font-black text-xl" style={{ textShadow: '0 0 12px rgba(0,0,0,0.9)', opacity: Math.min(1, hud.comboTimer / 30) }}>
              {hud.comboCount} HIT COMBO
            </div>
          </div>
        )}

        {/* Phase overlays */}
        {hud.phase === 'intro' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-30" data-testid="intro-overlay">
            <div className="text-yellow-400 font-black text-5xl mb-4" style={{ textShadow: '0 0 30px rgba(255,200,0,0.4)', animation: 'combatIntroSlide 0.6s ease-out' }}>
              ROUND {hud.round}
            </div>
            {hud.phaseTimer > 60 && hud.phaseTimer <= 120 && (
              <div className="text-white font-black text-6xl" style={{ textShadow: '0 0 25px rgba(255,255,255,0.3)', animation: 'combatKOSlam 0.4s ease-out' }}>
                READY?
              </div>
            )}
            {hud.phaseTimer > 120 && (
              <div className="text-red-500 font-black text-7xl" style={{ textShadow: '0 0 35px rgba(255,0,0,0.5)', animation: 'combatKOSlam 0.3s ease-out' }}>
                FIGHT!
              </div>
            )}
          </div>
        )}
        {hud.phase === 'ko' && hud.phaseTimer > 30 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid="ko-overlay">
            <div className="text-red-600 font-black text-7xl" style={{ textShadow: '0 0 30px rgba(255,0,0,0.5)', animation: 'combatKOSlam 0.5s ease-out' }}>K.O.</div>
          </div>
        )}
        {hud.phase === 'roundEnd' && hud.phaseTimer > 20 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50" data-testid="round-end-overlay">
            <div className={`font-black text-4xl ${hud.playerWins > hud.enemyWins ? 'text-cyan-400' : 'text-red-500'}`} style={{ textShadow: '0 0 20px rgba(0,0,0,0.8)' }}>
              {hud.playerWins > hud.enemyWins ? `${(gameTitle || 'PLAYER').toUpperCase()} WINS` : 'ENEMY WINS'}
            </div>
            <div className="text-yellow-400 font-bold mt-2">ROUND {hud.round}</div>
          </div>
        )}
        {hud.phase === 'matchEnd' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/65" data-testid="match-end-overlay">
            <div className={`font-black text-6xl ${hud.playerWins >= 2 ? 'text-yellow-400' : 'text-red-500'}`} style={{ textShadow: '0 0 25px rgba(0,0,0,0.9)' }}>
              {hud.playerWins >= 2 ? 'VICTORY' : 'DEFEAT'}
            </div>
            <div className="text-white font-bold text-xl mt-4">FINAL SCORE: {hud.score}</div>
            <div className="text-white/50 text-sm mt-2">ROUNDS WON: {hud.playerWins} - {hud.enemyWins}</div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes combatHitFloat {
          0% { transform: translateY(0) scale(1.3); opacity: 1; }
          100% { transform: translateY(-40px) scale(0.8); opacity: 0; }
        }
        @keyframes combatKOSlam {
          0% { transform: scale(3); opacity: 0; }
          50% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes combatIntroSlide {
          0% { transform: translateY(-60px) scale(0.5); opacity: 0; }
          60% { transform: translateY(5px) scale(1.05); }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default CombatMode;
